import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingBag, Heart, Menu, X, User } from 'lucide-react';
import { supabase } from "../supabaseClient";
import logo from '../assets/Siyaanishlogo1.png';
import { useCart } from '../context/CartContext';

const Header = ({ isAuth }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { cartCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-cream/95 backdrop-blur-md border-b border-[#E5E0D8]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">

          {/* LOGO */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={logo}
              alt="logo"
              className="h-16 w-auto bg-transparent mix-blend-multiply transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* DESKTOP NAV */}
          <nav className="hidden md:flex items-center gap-10 text-sm tracking-wider">

            <Link
              to="/"
              className={`${isActive('/') ? 'text-bronze border-b-2 border-[#ba814b]' : 'text-charcoal'} hover:text-[#ba814b] pb-1`}
            >
              HOME
            </Link>

            {/* CATEGORIES DROPDOWN */}
            <div className="relative group">
              <Link
                to="/categories"
                className="text-charcoal hover:text-[#ba814b] pb-1"
              >
                CATEGORIES
              </Link>

              <div className="absolute left-0 mt-3 w-48 bg-white shadow-lg rounded-md border z-50 
                              opacity-0 invisible group-hover:opacity-100 group-hover:visible 
                              transition-all duration-200">

                <Link to="/category/men" className="block px-4 py-2 hover:bg-gray-100">
                  Men
                </Link>

                <Link to="/category/women" className="block px-4 py-2 hover:bg-gray-100">
                  Women
                </Link>

                {/* HOMEWARE WITH SUBMENU */}
                <div className="relative group/sub">
                  <Link
  to="/category/homeware"
  className="block px-4 py-2 hover:bg-gray-100"
>
  Homeware →
</Link>

                  <div className="absolute left-full top-0 w-48 bg-white shadow-lg rounded-md border 
                                  opacity-0 invisible group-hover/sub:opacity-100 group-hover/sub:visible 
                                  transition-all duration-200">

                    <Link
                      to="/category/homeware/vanya"
                      className="block px-4 py-2 hover:bg-gray-100"
                    >
                      Vanya
                    </Link>

                  </div>
                </div>

              </div>
            </div>

            <Link
              to="/about"
              className={`${isActive('/about') ? 'text-bronze border-b-2 border-[#ba814b]' : 'text-charcoal'} hover:text-[#ba814b] pb-1`}
            >
              ABOUT
            </Link>

            <Link
              to="/contact"
              className={`${isActive('/contact') ? 'text-bronze border-b-2 border-[#ba814b]' : 'text-charcoal'} hover:text-[#ba814b] pb-1`}
            >
              CONTACT
            </Link>

            {!isAuth && (
              <Link
                to="/register"
                className={`${isActive('/register') ? 'text-bronze border-b-2 border-[#ba814b]' : 'text-charcoal'} hover:text-[#ba814b] pb-1`}
              >
                REGISTER
              </Link>
            )}

          </nav>

          {/* ICONS */}
          <div className="flex items-center gap-6">

            <Link to="/wishlist" className="hidden md:block text-charcoal hover:text-bronze">
              <Heart size={20} />
            </Link>

            {/* USER */}
            {isAuth ? (
              <div className="relative hidden md:block group">
                <User className="cursor-pointer text-charcoal hover:text-bronze" size={20} />

                <div className="absolute right-0 mt-3 w-40 bg-white shadow-lg rounded-md border z-50 
                                opacity-0 invisible group-hover:opacity-100 group-hover:visible">

                  <Link to="/profile" className="block px-4 py-2 hover:bg-gray-100">Profile</Link>
                  <Link to="/orders" className="block px-4 py-2 hover:bg-gray-100">Orders</Link>
                  {/* <Link to="/settings" className="block px-4 py-2 hover:bg-gray-100">Settings</Link> */}

                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <Link to="/login" className="hidden md:block text-charcoal hover:text-bronze">
                <User size={20} />
              </Link>
            )}

            {/* CART */}
            <Link to="/cart" className="relative text-charcoal hover:text-bronze">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#ba8245] text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* MOBILE MENU BUTTON */}
            <button className="md:hidden" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

          </div>
        </div>

        {/* MOBILE MENU */}
        {isMenuOpen && (
          <nav className="md:hidden py-6 border-t">
            <div className="flex flex-col gap-4 text-sm">

              <Link to="/" onClick={() => setIsMenuOpen(false)}>HOME</Link>

              <Link to="/categories" onClick={() => setIsMenuOpen(false)}>
                CATEGORIES
              </Link>

              <Link to="/category/men" className="pl-4" onClick={() => setIsMenuOpen(false)}>
                - Men
              </Link>

              <Link to="/category/women" className="pl-4" onClick={() => setIsMenuOpen(false)}>
                - Women
              </Link>

              <Link to="/category/homeware" className="pl-4" onClick={() => setIsMenuOpen(false)}>
                - Homeware
              </Link>

              <Link to="/category/homeware/vanya" className="pl-8" onClick={() => setIsMenuOpen(false)}>
                -- Vanya
              </Link>

              <Link to="/about" onClick={() => setIsMenuOpen(false)}>ABOUT</Link>
              <Link to="/contact" onClick={() => setIsMenuOpen(false)}>CONTACT</Link>

              {!isAuth && (
                <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                  REGISTER
                </Link>
              )}

            </div>
          </nav>
        )}

      </div>
    </header>
  );
};

export default Header;